/**
 * Client-side persistence + URL parsing for Skyscanner day view deeplink attribution.
 * See traveleasy-cms src/modules/metasearch/README.md § Day View Deeplink Attribution.
 *
 * sessionStorage (not localStorage) on purpose: it must survive the shopping session
 * (date/flight changes, re-search) but must NOT leak into a later, unrelated session.
 */

const SKYSCANNER_REDIRECT_ID_KEY = "skyscanner_redirectid"
const DAYVIEW_LANDING_RECORDED_KEY = "skyscanner_dayview_landing_recorded"

export function persistSkyscannerRedirectId(id: string): void {
  if (typeof sessionStorage === "undefined") return
  sessionStorage.setItem(SKYSCANNER_REDIRECT_ID_KEY, id)
}

export function getPersistedSkyscannerRedirectId(): string | null {
  if (typeof sessionStorage === "undefined") return null
  return sessionStorage.getItem(SKYSCANNER_REDIRECT_ID_KEY)
}

const SKYSCANNER_TIMEOUT_BYPASS_PREFIX = "skyscanner_timeout_bypass_used_for:"

/**
 * Temporary hotfix helper for the client-side session-timeout popups (search/booking timers
 * false-fire on stale `current_time_session`/`startedSearchAt` when Skyscanner lands a user
 * directly on search, passenger or checkout, since those markers weren't reset for this new
 * redirect). Resolves the current Skyscanner redirect id the same way as before — already
 * persisted this tab session, or from the current URL's own query string, persisting it if so
 * — but instead of bypassing forever, each redirect id may bypass a given timer (`timerKey`) at
 * most ONCE: a fresh `skyscanner_redirectid` (one this timer hasn't bypassed before) skips the
 * stale-session popup, but once consumed, a later real expiry during that SAME redirect's
 * session shows the popup normally, exactly like any other visitor. A new redirect (e.g. the
 * user clicks another Skyscanner deeplink later) gets its own fresh bypass.
 */
export function consumeSkyscannerTimeoutBypass(
  timerKey: "booking" | "search",
  searchParams?: { get(name: string): string | null }
): boolean {
  const params = searchParams ?? (typeof window !== "undefined" ? new URLSearchParams(window.location.search) : undefined)
  const idFromUrl = params?.get(SKYSCANNER_REDIRECT_ID_KEY)?.trim()
  if (idFromUrl) {
    persistSkyscannerRedirectId(idFromUrl)
  }
  const currentId = idFromUrl || getPersistedSkyscannerRedirectId()
  if (!currentId) return false
  if (typeof sessionStorage === "undefined") return false

  const usedKey = SKYSCANNER_TIMEOUT_BYPASS_PREFIX + timerKey
  if (sessionStorage.getItem(usedKey) === currentId) return false

  sessionStorage.setItem(usedKey, currentId)
  return true
}

export function isDayviewLandingRecorded(id: string): boolean {
  if (typeof sessionStorage === "undefined") return false
  return sessionStorage.getItem(DAYVIEW_LANDING_RECORDED_KEY) === id
}

export function markDayviewLandingRecorded(id: string): void {
  if (typeof sessionStorage === "undefined") return
  sessionStorage.setItem(DAYVIEW_LANDING_RECORDED_KEY, id)
}

/**
 * `language` is never a query param on the day view deeplink (generateDayViewDeeplink()
 * encodes it into the path instead — /vi/flights/result vs /en/flights/result), so it has
 * to be read off the pathname. Matches the "VI"/"EN" uppercase convention already used by
 * MetasearchFlightSearchDto.language / RecordDayviewLandingDto.language.
 */
export function getLanguageFromPathname(pathname: string | null | undefined): string | undefined {
  if (pathname?.startsWith("/en")) return "EN"
  if (pathname?.startsWith("/vi")) return "VI"
  return undefined
}

/**
 * POST body for /v1/flights/metasearch/dayview-landing — forwards every non-empty query
 * param present on the landing URL (route, date, pax, utm tags, ... included), not just a
 * curated subset: the DTO accepts arbitrary extra fields, and this mirrors how the standard
 * deeplink flow already forwards its full query string (buildMetasearchBookingBody).
 *
 * Two fields aren't on the query string and must be added explicitly (matches
 * RecordDayviewLandingDto field names exactly):
 * - `language` (see getLanguageFromPathname) — applied only when not already on the query
 *   string, since the deeplink never puts it there.
 * - `day_view_links` (full `window.location.href`) — the day view deeplink / landing page
 *   URL the user arrived on, kept by BE for audit purposes. `deeplinks` (the standard
 *   per-flight deeplink) is the sibling field for when the FE has one on hand — it never
 *   does at day-view landing time (no flight/fare picked yet), so this call omits it.
 */
export function buildDayviewLandingBody(
  searchParams: URLSearchParams,
  landingUrl?: string,
  language?: string
): Record<string, string> | null {
  const skyscannerRedirectId = searchParams.get(SKYSCANNER_REDIRECT_ID_KEY)?.trim()
  if (!skyscannerRedirectId) return null

  const body: Record<string, string> = {}
  for (const [key, value] of searchParams.entries()) {
    const trimmed = value.trim()
    if (trimmed) body[key] = trimmed
  }
  body[SKYSCANNER_REDIRECT_ID_KEY] = skyscannerRedirectId
  if (landingUrl) body.day_view_links = landingUrl
  if (language && !body.language) body.language = language
  return body
}
